import { language, t } from './i18n';
import { TEMPLATES } from '../i18n/templates';

/**
 * Text written by the server, shown in the visitor's language.
 *
 * The API answers errors in English and stores notifications in French, both
 * with names and titles baked in. Each known sentence is listed here as a
 * template; a message matching one is rewritten through the French phrase that
 * t() already knows, with the baked-in values carried across. Anything unknown
 * falls back to a general sentence for its error code, so a French or Arabic
 * visitor never meets a stray English message.
 */

type Rule = { pattern: RegExp; names: string[]; french: string };

const escape = (text: string) => text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

function compile(template: string, french: string): Rule {
  const names: string[] = [];
  const source = template
    .split(/(\{\w+\})/)
    .map((part) => {
      const name = /^\{(\w+)\}$/.exec(part)?.[1];
      if (!name) return escape(part);
      names.push(name);
      return '([\\s\\S]+?)';
    })
    .join('');
  return { pattern: new RegExp(`^${source}$`), names, french };
}

function rewrite(rules: Rule[], text: string): string | null {
  for (const rule of rules) {
    const match = rule.pattern.exec(text.trim());
    if (!match) continue;
    const params: Record<string, string> = {};
    rule.names.forEach((name, index) => {
      params[name] = match[index + 1] ?? '';
    });
    return t(rule.french, params);
  }
  return null;
}

/** API error messages (English) → the French phrase that t() translates. */
const ERRORS: [string, string][] = [
  ['Email, phone or password is incorrect', 'Email, téléphone ou mot de passe incorrect.'],
  ['You cannot do that', 'Vous ne pouvez pas faire cela.'],
  ['Too many requests', 'Trop de tentatives. Réessayez dans quelques minutes.'],
  ['Too many attempts. Try again in a few minutes.', 'Trop de tentatives. Réessayez dans quelques minutes.'],
  ['Something went wrong', 'Une erreur est survenue'],
  ['Authentication required', 'Connectez-vous pour continuer.'],
  ['Your session has expired', 'Votre session a expiré. Reconnectez-vous.'],
  ['Your session has expired. Please log in again.', 'Votre session a expiré. Reconnectez-vous.'],
  ['This session was signed out for security. Please log in again.', 'Par sécurité, cette session a été fermée. Reconnectez-vous.'],
  ['Invalid or expired token', 'Votre session a expiré. Reconnectez-vous.'],
  ['Account no longer exists', 'Ce compte n’existe plus.'],
  ['That account no longer exists', 'Ce compte n’existe plus.'],
  ['This account is suspended', 'Ce compte est suspendu.'],
  ['This account is not active', 'Ce compte n’est pas actif.'],
  ['Identity verification must be approved first', 'Votre identité doit d’abord être vérifiée.'],
  ['This action needs the {roles} role', 'Votre compte n’a pas accès à cette action.'],
  ['You do not own {what}', 'Cet élément ne vous appartient pas.'],
  ['{what} not found', 'Introuvable : il a peut-être été supprimé.'],
  ['That code is already taken', 'Ce code est déjà pris.'],
  ['Could not generate a free code — try again', 'Impossible de générer un code libre — réessayez.'],
  ['An account already exists with this email', 'Un compte existe déjà avec cet email.'],
  ['An account already exists with this phone number', 'Un compte existe déjà avec ce numéro de téléphone.'],
  ['That number already belongs to another account', 'Ce numéro appartient déjà à un autre compte.'],
  ['Unknown affiliate code', 'Code d’affiliation inconnu.'],
  ['No pending code for this address', 'Aucun code en attente pour cette adresse. Demandez-en un nouveau.'],
  ['That code has expired — ask for a new one', 'Ce code a expiré — demandez-en un nouveau.'],
  ['Too many wrong codes — ask for a new one', 'Trop de codes erronés — demandez-en un nouveau.'],
  ['That code is not right', 'Ce code n’est pas correct.'],
  ['That reset link is not valid', 'Ce lien de réinitialisation n’est pas valide.'],
  ['That reset link has expired', 'Ce lien de réinitialisation a expiré.'],
  ['Your current password is not right', 'Votre mot de passe actuel n’est pas correct.'],
  ['We could not send the email. Try again in a moment.', 'L’email n’a pas pu être envoyé. Réessayez dans un instant.'],
  ['Google sign-in is not configured on this server', 'La connexion Google n’est pas disponible pour le moment.'],
  ['That Google sign-in could not be verified', 'La connexion Google n’a pas pu être vérifiée.'],
  ['Google did not return an email address', 'Google n’a pas fourni d’adresse email.'],
  ['Verify your email address with Google before signing in with it', 'Vérifiez votre adresse email auprès de Google avant de vous connecter avec.'],
  ['"to" must come after "from"', 'La date de fin doit être après la date de début.'],
  ['The end date must come after the start date', 'La date de fin doit être après la date de début.'],
  ['endDate must come after startDate', 'La date de fin doit être après la date de début.'],
  ['Ask for at most {count} nights at a time', 'Demandez au plus {count} nuits à la fois.'],
  ['A stay needs at least one night', 'Un séjour compte au moins une nuit.'],
  ['Those dates have just been taken for this listing', 'Ces dates viennent d’être prises pour ce logement.'],
  ['Those dates are not available', 'Ces dates ne sont pas disponibles.'],
  ['A booking cannot go from {from} to {to}', 'Cette réservation ne peut plus changer de statut.'],
  ['This listing is not offered for {mode} stays', 'Ce logement n’est pas proposé pour ce type de séjour.'],
  ['A nightly stay needs an end date', 'Un séjour à la nuit demande une date de départ.'],
  ['This listing is not open for bookings', 'Ce logement n’accepte pas de réservations pour le moment.'],
  ['You cannot book your own listing', 'Vous ne pouvez pas réserver votre propre logement.'],
  ['Verify your email address before booking', 'Vérifiez votre adresse email avant de réserver.'],
  ['The start date is in the past', 'La date de début est déjà passée.'],
  ['This place takes at most {count} guest(s)', 'Ce logement accueille au plus {count} personne(s).'],
  ['This booking is not yours', 'Cette réservation ne vous appartient pas.'],
  ['This booking is already confirmed', 'Cette réservation est déjà confirmée.'],
  ['The host has not accepted this request yet', 'L’hôte n’a pas encore accepté cette demande.'],
  ['This booking has nothing to pay online', 'Cette réservation n’a rien à payer en ligne.'],
  ['Unknown payment reference', 'Référence de paiement inconnue.'],
  ['You already have 3 active requests — pause one first', 'Vous avez déjà 3 demandes actives — mettez-en une en pause d’abord.'],
  ['This request is not yours', 'Cette demande ne vous appartient pas.'],
  ['The maximum budget cannot be below the minimum', 'Le budget maximum ne peut pas être inférieur au minimum.'],
  ['This is your own request', 'C’est votre propre demande.'],
  ['Your identity is already verified', 'Votre identité est déjà vérifiée.'],
  ['A business account needs its 15-digit ICE number', 'Un compte société doit indiquer son numéro ICE à 15 chiffres.'],
  ['An ICE number is exactly 15 digits', 'Un numéro ICE compte exactement 15 chiffres.'],
  ['You already have a verification in progress', 'Une vérification est déjà en cours.'],
  ['This verification has already been decided', 'Cette vérification a déjà été traitée.'],
  ['Start a conversation from a listing or a colocation request', 'Commencez une conversation depuis une annonce ou une demande de colocation.'],
  ['You cannot message yourself', 'Vous ne pouvez pas vous écrire à vous-même.'],
  ['This conversation is not yours', 'Cette conversation ne vous appartient pas.'],
  ['This account cannot send messages', 'Ce compte ne peut pas envoyer de messages.'],
  ['Verify your email address before sending messages', 'Vérifiez votre adresse email avant d’envoyer des messages.'],
  ['Attach a file in the "file" field', 'Choisissez un fichier.'],
  ['This listing is not yours', 'Ce logement ne vous appartient pas.'],
  ['That pin is outside Morocco', 'Ce point est hors du Maroc.'],
  ['This listing is already live', 'Cette annonce est déjà en ligne.'],
  ['This listing was removed', 'Cette annonce a été supprimée.'],
  ['A removed listing cannot be changed', 'Une annonce supprimée ne peut plus être modifiée.'],
  ['This listing has active bookings — pause it instead of deleting it', 'Cette annonce a des réservations en cours — mettez-la en pause au lieu de la supprimer.'],
  ['This room is not yours', 'Cette chambre ne vous appartient pas.'],
  ['This room has active bookings', 'Cette chambre a des réservations en cours.'],
  ['A price is required to enable a rental mode', 'Indiquez un prix pour activer ce type de location.'],
  ['The maximum stay cannot be shorter than the minimum', 'La durée maximale ne peut pas être plus courte que la minimale.'],
  ['A listing can hold {max} videos', 'Une annonce peut contenir {max} vidéos au maximum.'],
  ['A listing can hold {max} photos', 'Une annonce peut contenir {max} photos au maximum.'],
  ['This photo is not yours', 'Cette photo ne vous appartient pas.'],
  ['The exact address unlocks once your deposit is paid', 'L’adresse exacte s’affiche une fois les frais de service réglés.'],
  ['You can review once the stay is finished', 'Vous pourrez laisser un avis une fois le séjour terminé.'],
  ['You have already reviewed this stay', 'Vous avez déjà laissé un avis sur ce séjour.'],
  ['minPriceCentimes is greater than maxPriceCentimes', 'Le prix minimum est supérieur au prix maximum.'],
  ['A Moroccan RIB must be exactly 24 digits', 'Un RIB marocain compte exactement 24 chiffres.'],
  ['Video is larger than {max} MB', 'Vidéo trop lourde : {max} Mo maximum. Filmez plus court ou en 720p.'],
  ['That file is an image, not a video', 'Ce fichier est une image, pas une vidéo.'],
  ['That file is not an MP4, MOV or WebM video', 'Ce fichier n’est pas une vidéo MP4, MOV ou WebM.'],
  ['Only images and videos can be stored', 'Seules les images et les vidéos sont acceptées.'],
  ['Image is larger than {max} MB', 'Image trop lourde : {max} Mo maximum.'],
  ['That file is not a JPEG, PNG, WebP or AVIF image', 'Ce fichier n’est pas une image JPEG, PNG, WebP ou AVIF.'],
  ['Some fields are invalid', 'Certains champs sont invalides.'],
  ['Identity verification must be approved before publishing', 'Faites vérifier votre identité avant de publier.'],
  ['Verify your email address', 'Vérifiez votre adresse email.'],
  ['Add at least {count} photos', 'Ajoutez au moins {count} photos.'],
  ['Enable at least one rental mode with a price', 'Activez au moins un type de location avec un prix.'],
  ['Place the pin and name the neighbourhood', 'Placez le point sur la carte et indiquez le quartier.'],
  ['This listing is not ready to publish', 'Cette annonce n’est pas encore prête à être publiée.'],
  ['Our team reviews documents within 24 to 48 hours.', 'Notre équipe vérifie les documents sous 24 à 48 heures.'],
  ['That value is already taken', 'Cette valeur est déjà utilisée.'],
  ['Provide a phone number or an email', 'Indiquez un email ou un numéro de téléphone.'],
  ['Enter a street address', 'Indiquez l’adresse.'],
  ['Enter a valid email address', 'Entrez une adresse email valide.'],
  ['Enter your password', 'Entrez votre mot de passe.'],
  ['Give your listing a title', 'Donnez un titre à votre annonce.'],
  ['Letters, digits, - or _, 3 to 32 characters', 'Lettres, chiffres, - ou _, de 3 à 32 caractères.'],
  ['Tell us your name', 'Indiquez votre nom.'],
  ['The code is 6 digits', 'Le code compte 6 chiffres.'],
  ['Use at least 8 characters', 'Au moins 8 caractères.'],
  ['Use the YYYY-MM-DD format', 'Date invalide.'],
  ['A Moroccan RIB is exactly 24 digits', 'Un RIB marocain compte exactement 24 chiffres.'],
  ['must NOT have fewer than {n} characters', 'Au moins {n} caractères.'],
  ['must NOT have more than {n} characters', 'Au plus {n} caractères.'],
  ['must NOT have fewer than {n} items', 'Au moins {n} éléments.'],
  ['must NOT have more than {n} items', 'Au plus {n} éléments.'],
  ['must have required property {name}', 'Champ obligatoire.'],
  ['must match format "email"', 'Entrez une adresse email valide.'],
  ['must match format {format}', 'Format invalide.'],
  ['must match pattern {pattern}', 'Format invalide.'],
  ['must be >= {n}', 'Doit être au moins {n}.'],
  ['must be <= {n}', 'Doit être au plus {n}.'],
  ['must be > {n}', 'Doit être supérieur à {n}.'],
  ['must be < {n}', 'Doit être inférieur à {n}.'],
  ['must be equal to one of the allowed values', 'Choisissez une valeur de la liste.'],
  ['must be {type}', 'Valeur invalide.'],
  ['Invalid value', 'Valeur invalide.'],
];

/** The sentence for an error code, when the message itself is not known. */
const BY_CODE: Record<string, string> = {
  VALIDATION_ERROR: 'Certains champs sont invalides.',
  UNAUTHENTICATED: 'Connectez-vous pour continuer.',
  TOKEN_EXPIRED: 'Votre session a expiré. Reconnectez-vous.',
  TOKEN_REUSED: 'Par sécurité, cette session a été fermée. Reconnectez-vous.',
  INVALID_CREDENTIALS: 'Email, téléphone ou mot de passe incorrect.',
  FORBIDDEN: 'Vous ne pouvez pas faire cela.',
  KYC_REQUIRED: 'Votre identité doit d’abord être vérifiée.',
  ROLE_REQUIRED: 'Votre compte n’a pas accès à cette action.',
  NOT_FOUND: 'Introuvable : il a peut-être été supprimé.',
  CONFLICT: 'Cette action n’est plus possible.',
  EMAIL_TAKEN: 'Un compte existe déjà avec cet email.',
  PHONE_TAKEN: 'Un compte existe déjà avec ce numéro de téléphone.',
  OTP_INVALID: 'Ce code n’est pas correct.',
  OTP_EXPIRED: 'Ce code a expiré — demandez-en un nouveau.',
  OTP_THROTTLED: 'Trop de codes erronés — demandez-en un nouveau.',
  DATES_UNAVAILABLE: 'Ces dates ne sont pas disponibles.',
  INVALID_TRANSITION: 'Cette réservation ne peut plus changer de statut.',
  PAYMENT_REQUIRED: 'Un paiement est nécessaire pour continuer.',
  RATE_LIMITED: 'Trop de tentatives. Réessayez dans quelques minutes.',
  PAYLOAD_TOO_LARGE: 'Fichier trop lourd.',
  UNSUPPORTED_MEDIA_TYPE: 'Format de fichier non pris en charge.',
  PROVIDER_ERROR: 'Un service externe ne répond pas. Réessayez dans un instant.',
};

const errorRules = ERRORS.map(([english, french]) => compile(english, french));

/** An API error message in the visitor's language. */
export function translateApiError(message: string, code: string): string {
  const known = rewrite(errorRules, message);
  if (known) return known;
  // English visitors can read the server's own words; the others get the
  // general sentence for the code rather than English.
  if (language === 'en' && message) return message;
  return t(BY_CODE[code] ?? 'Une erreur est survenue');
}

/** Notification titles and bodies, stored in French by the server. */
// Shared with the server, which uses the same templates to write emails.
const NOTIFICATIONS = TEMPLATES.map((french) => compile(french, french));

/**
 * Any other line from the server: a field error or a checklist item in English,
 * or a confirmation written in French.
 */
export function translateServerText(text: string): string {
  if (!text) return text;
  const known = rewrite(errorRules, text);
  if (known) return known;
  if (language === 'en' && /^[ -~’—]+$/.test(text)) return text;
  if (/^[ -~’—]+$/.test(text)) return t('Valeur invalide.');
  return translateNotification(text);
}

/** A stored notification line in the visitor's language. */
export function translateNotification(text: string): string {
  if (language === 'fr' || !text) return text;
  return rewrite(NOTIFICATIONS, text) ?? t(text);
}
